import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EmptySectionCard,
  FireflyButton,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  LAST_DAY_EVENT_LABEL,
  NEXT_DAY_EVENT_LABEL,
} from '~/lib/constants/schedule.constants';
import { formatFullDateString } from '~/lib/util/schedule/schedule.util';

import EventDayCard from './EventDayCard';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  EditAppointmentEvents,
  EditAppointmentSource,
} from '~/analytics/patientEvents';
import { useIsPatientPath } from '~/lib/hooks/useIsPatientPath';

const TOP_LEVEL_ID = 'schedule-day-view';

interface ScheduleDayViewProps {
  lastScheduleDate?: string;
  nextScheduleDate?: string;
  onCancelAppointment?: (externalId: string) => void;
  onFetchGroupClassEvents?: () => void;
  onNextScheduleDateClicked?: (nextScheduleDate: string) => void;
  onNotesClicked?: (externalId: string) => void;
  onScheduleItemClicked: (externalId: string) => void;
  onZoomClicked?: (externalId: string) => void;
  scheduledItems: ScheduleItemBase[];
  onCloseEventDetailsModal: () => void;
}

const ScheduleDayView = ({
  lastScheduleDate,
  nextScheduleDate,
  onCancelAppointment,
  onNextScheduleDateClicked,
  onNotesClicked,
  onScheduleItemClicked,
  onZoomClicked,
  scheduledItems,
  onCloseEventDetailsModal,
}: ScheduleDayViewProps) => {
  const { trackEvent } = useAnalytics();
  const isPatientPath = useIsPatientPath();

  const getNextOrLastClassDateText = () => {
    const scheduleDate = nextScheduleDate ?? lastScheduleDate;
    const handleNextScheduleDateClicked = () => {
      const source = isPatientPath
        ? EditAppointmentSource.patientCalendarTab
        : EditAppointmentSource.providerCalendarTab;
      trackEvent(EditAppointmentEvents.nextOrLastDayWithEventsClicked, {
        source,
      });
      onNextScheduleDateClicked?.(scheduleDate);
    };
    return (
      scheduleDate && (
        <ChakraFlex
          alignItems="center"
          justifyContent="center"
          paddingTop="24px"
        >
          <ChakraText
            color="neutral"
            fontWeight="400"
            id={`${TOP_LEVEL_ID}-primary-text`}
          >
            {nextScheduleDate ? NEXT_DAY_EVENT_LABEL : LAST_DAY_EVENT_LABEL}
            &nbsp;
          </ChakraText>
          <FireflyButton
            id={`${TOP_LEVEL_ID}-button`}
            onClick={handleNextScheduleDateClicked}
            variant="tertiary"
          >
            {`${formatFullDateString(DateTime.fromISO(scheduleDate))}`}
          </FireflyButton>
        </ChakraFlex>
      )
    );
  };

  const NoEventsContent = (
    <ChakraBox padding="6px 5px">
      <EmptySectionCard caption="No events on this day" id={TOP_LEVEL_ID} />
      {getNextOrLastClassDateText()}
    </ChakraBox>
  );

  return (
    <ChakraFlex direction="column" marginBottom="6px" overflow="none">
      {scheduledItems?.length > 0
        ? scheduledItems.map((scheduleItem) => (
            <EventDayCard
              key={scheduleItem.externalId}
              onCancelAppointment={onCancelAppointment}
              onEventCardClick={onScheduleItemClicked}
              onNextScheduleDateClicked={onNextScheduleDateClicked}
              onNoteButtonClick={onNotesClicked}
              onZoomClick={onZoomClicked}
              scheduleItem={scheduleItem}
              shouldCloseModal={onCloseEventDetailsModal}
            />
          ))
        : NoEventsContent}
    </ChakraFlex>
  );
};

export default ScheduleDayView;
